// Underwriter-review actions for the single-scheme display: debounced premium override, policy-level endorsements, decline, finalise.
import { ref, computed, onMounted, type ComputedRef } from 'vue';
import { useRiskStore } from '@/stores/riskData';
import { UpdateQuote } from '@/composables/useQuoteAPIRequests';
import { GetProductEndorsements, UpdateProductOutput, postNavigate } from '@/composables/useReviewApi';

export function useReviewMode(opts: {
    productId: string;
    action: string;
    quoteId: string;
    isReview: ComputedRef<boolean>;
    scheme: ComputedRef<any>;
    schemeId: ComputedRef<any>;
}) {
    const store = useRiskStore();
    const isBusy = ref(false);

    // ---- premium override (cover-level /update, server recomputes IPT/total) ----
    // Debounced per cover so rapid steps (e.g. clicking the stepper 5×) collapse into one request.
    const overrideTimers = new Map<number, ReturnType<typeof setTimeout>>();
    async function doPersistCover(cover: any) {
        try {
            const { UpdateQuote: out } = await UpdateQuote(opts.action, opts.productId, opts.quoteId, {
                schemeId: opts.schemeId.value, page: 4, showReviewLists: true, addonQuoteRequest: false, cover, selectOnly: false,
            });
            if (out) store.setPayload({ ...(store.payload ?? {}), productOutput: out, action: opts.action ?? '' });
        } finally { if (overrideTimers.size === 0) isBusy.value = false; }
    }
    function persistCover(cover: any) {
        const id = cover?.CoverId;
        const existing = overrideTimers.get(id);
        if (existing) clearTimeout(existing);
        isBusy.value = true;
        overrideTimers.set(id, setTimeout(() => { overrideTimers.delete(id); void doPersistCover(cover); }, 400));
    }

    // ---- policy-level endorsements (scheme.Endorsements via /updateproductoutput) ----
    const endoList = ref<any[]>([]);
    onMounted(async () => { if (opts.isReview.value) endoList.value = await GetProductEndorsements(opts.productId); });
    const filteredEndos = computed(() => endoList.value.filter((e: any) => !opts.schemeId.value || e.SchemeId === opts.schemeId.value));
    const policyEndorsements = computed<any[]>(() => opts.scheme.value?.Endorsements ?? []);

    async function persistProductOutput() {
        isBusy.value = true;
        try {
            const out = await UpdateProductOutput(opts.action, opts.productId, opts.quoteId, store.productOutput);
            if (out) store.setPayload({ ...(store.payload ?? {}), productOutput: out, action: opts.action ?? '' });
        } finally { isBusy.value = false; }
    }

    // One modal serves both levels: a cover target rides /update; a null target is policy-level (/updateproductoutput).
    const addOpen = ref(false);
    const addTarget = ref<any>(null);
    const openAddEndorsement = (cover?: any) => { addTarget.value = cover ?? null; addOpen.value = true; };
    async function onEndoSubmit(endorsement: any) {
        addOpen.value = false;
        const cover = addTarget.value;
        if (cover) {
            cover.Endorsements = [...(cover.Endorsements ?? []), endorsement];
            await persistCover(cover);
            return;
        }
        const scheme = opts.scheme.value;
        if (!scheme) return;
        scheme.Endorsements = [...(scheme.Endorsements ?? []), endorsement];
        await persistProductOutput();
    }
    async function removeCoverEndorsement({ cover, endorsement }: { cover: any; endorsement: any }) {
        cover.Endorsements = (cover.Endorsements ?? []).filter((e: any) => e !== endorsement);
        await persistCover(cover);
    }
    async function removePolicyEndorsement(endorsement: any) {
        const scheme = opts.scheme.value;
        if (!scheme) return;
        scheme.Endorsements = (scheme.Endorsements ?? []).filter((e: any) => e !== endorsement);
        await persistProductOutput();
    }

    // ---- decline ----
    const declineOpen = ref(false);
    const declineReason = ref('');
    const submitDecline = () => postNavigate('/quotes/decline', { QuoteId: opts.quoteId, Action: opts.action, DeclineReason: declineReason.value });

    // ---- finalise review ----
    const finaliseOpen = ref(false);
    const reviewComments = ref('');
    const submitFinalise = (purchase: boolean) => postNavigate('/quotes/review', {
        QuoteId: opts.quoteId,
        ProductId: opts.productId,
        Action: opts.action,
        RiskDataJson: JSON.stringify(store.riskData ?? {}),
        SchemeOutputJson: JSON.stringify(store.productOutput ?? {}),
        purchase: String(purchase),
        ReviewComments: reviewComments.value,
    });

    return {
        isBusy, persistCover,
        filteredEndos, policyEndorsements, addOpen, openAddEndorsement, onEndoSubmit, removeCoverEndorsement, removePolicyEndorsement,
        declineOpen, declineReason, submitDecline,
        finaliseOpen, reviewComments, submitFinalise,
    };
}
